import { useCallback, useMemo, useRef, useState } from 'react';
import { Stripe } from '@stripe/stripe-js';
import pRetry from 'p-retry';
import appConfig from '@frontend/env';
import { i18next } from '@frontend/i18n';
import { paymentsSentry } from '@frontend/payments-hooks';
import { useAlert } from '@frontend/design-system';
import { useMounted } from './use-mounted';

type StripePromise = Promise<Stripe | null>;

const STRIPE_KEY = appConfig.STRIPE_EXPRESS_API_KEY;

export const STRIPE_LOADING_FAILED_MSG = 'Failed to load Stripe.js within maximum retries.';
const STRIPE_UI_ERROR_MSG = 'Failed to load Stripe.';
const stripeErrorMessage = i18next.t(STRIPE_UI_ERROR_MSG, { ns: 'payments' });

// Only cache successful loads; failed promises should not be cached
const stripePromiseCache = new Map<string, StripePromise>();

interface UseStripePromiseOptions {
  stripeExpressKey?: string;
}

/**
 * @deprecated "isLive" and "StripeKey" never change.
 * We don't need a key cache or most of this.
 */
export const useStripePromise = ({ stripeExpressKey: stripeKey = STRIPE_KEY }: UseStripePromiseOptions = {}) => {
  const [error, setError] = useState<unknown>();
  const [isLoading, setIsLoading] = useState(false);
  const { runIfMounted } = useMounted();
  const alerts = useAlert();
  const abortControllerRef = useRef<AbortController | null>(null);

  const loadStripeSdk = useCallback(
    async (stripeKey: string): StripePromise => {
      runIfMounted((abortController) => {
        abortControllerRef.current = abortController;
        setIsLoading(true);
      });

      try {
        const { loadStripe } = await import('@stripe/stripe-js');

        // Wrap the pRetry attempt with abort signal to allow cancellation on unmount
        const stripe = await Promise.race([
          pRetry(() => loadStripe(stripeKey), {
            retries: 5,
            minTimeout: 1000,
            maxTimeout: 10000,
            factor: 2,
          }),
          new Promise<never>((_, reject) => {
            abortControllerRef.current?.signal.addEventListener('abort', () => {
              reject(new DOMException('Stripe loading cancelle via listener', 'AbortError'));
            });
            if (abortControllerRef.current?.signal.aborted) {
              reject(new DOMException('Stripe loading cancelled on attempt', 'AbortError'));
            }
          }),
        ]);

        runIfMounted(() => setIsLoading(false));
        return stripe;
      } catch (error) {
        // Never cache failed promises - allow future retry attempts
        stripePromiseCache.delete(stripeKey);

        const errorMsg = error instanceof Error ? error.message : JSON.stringify(error);
        const isAborted = error instanceof DOMException && error.name === 'AbortError';

        if (!isAborted) {
          paymentsSentry.error(
            STRIPE_LOADING_FAILED_MSG,
            'useStripePromise',
            `Error: ${errorMsg}, Retries: 5, Key length: ${stripeKey?.length || 0}`
          );
          console.error(STRIPE_LOADING_FAILED_MSG, error);
        }

        runIfMounted(() => {
          if (!isAborted) {
            setError(error);
          }
          setIsLoading(false);
        });

        return null;
      }
    },
    [runIfMounted]
  );

  // Use useMemo to get or create the singleton promise
  const stripePromise = useMemo(() => {
    if (!stripeKey) return Promise.resolve(null);
    const existingPromise = stripePromiseCache.get(stripeKey);

    if (existingPromise) return existingPromise;

    const newPromise = loadStripeSdk(stripeKey);
    // Only cache successful results by filtering in the cache check above
    stripePromiseCache.set(stripeKey, newPromise);
    return newPromise;
  }, [stripeKey, loadStripeSdk]);

  const alertStripeError = useCallback(() => {
    if (error) {
      alerts.error(stripeErrorMessage);
    }
  }, [error]);

  return { stripePromise, stripeKey, error, isLoading, alertStripeError };
};

// Used in unit testing
export const clearStripePromiseCache = () => {
  stripePromiseCache.clear();
};
